import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@components/global/Layout';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type {
  GetStaticPropsContext,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import getCategoryPageByUid from '@adapters/prismic/fetching/categoryPage/getCategoryPageByUid';
import CategoryPageSliceZone from '@components/cms/CategoryPageSliceZone';
import { handlePromoDetailsTracking } from '@utils/gtm';
import {
  AlgonomyPlacement,
  RecommendationsLoader,
} from '@components/product/ProductCollectionComponent';
import { CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary } from 'prismicio-types';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import {
  getAlgonomyPlacementCategories,
  logAlgonomyPlacementCategoryConflict,
} from '@adapters/prismic/utils/getAlgonomyPlacementCategory';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { getSliceProducts } from '@adapters/prismic/fetching/utils/getSliceProducts';
import { getSlicePromotions } from '@adapters/prismic/fetching/utils/getSlicePromotions';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';
import { getCompareTables } from '@adapters/prismic/fetching/utils/getCompareTables';
import { CompareTablesById } from '@adapters/prismic/utils/mapCompareTableData';
import type { HitProps } from '@components/algolia/Hit';

interface CategoryPageProps {
  categoryPage: any;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  stores: MyStoreData[] | null;
  recommendationsCategory: AlgonomyPlacement | null;
  sliceProducts: Record<string, HitProps>;
  slicePromotions: Record<number, BundlePromotion>;
  compareTables: CompareTablesById;
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<CategoryPageProps>
> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Branded Store page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const categoryPage = await getCategoryPageByUid(
    params.slug,
    pageLocale,
    previewRef
  );

  if (!categoryPage) {
    throw new Error(`Category page with slug '${params?.slug}' not found`);
  }

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    // 'quickAdd' namespace is required by QuickAdd (rendered inside a
    // category_page's image_hotspot slice's ProductTile), and 'comparison' by the
    // compare_table slice's tables — without them, t('quickAdd.cta') etc.
    // render as their raw i18n keys.
    serverSideTranslations(locale, ['common', 'quickAdd', 'comparison']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/category/${params.slug}`);
  const langAltUrl = localeAltPathAbs(locale, `/category/${params.slug}`);
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  // When present, an algonomy_placement's category takes over the page's
  // request instead of falling back to best_seller_recommendations, so this
  // slice never silently depends on another slice's data.
  const algonomyPlacementCategories = getAlgonomyPlacementCategories(
    categoryPage.body
  );
  logAlgonomyPlacementCategoryConflict(
    'Category page',
    params.slug,
    algonomyPlacementCategories
  );

  const recommendationsCategory =
    algonomyPlacementCategories[0] ??
    (
      categoryPage.body.find(
        (slice: { slice_type: string }) =>
          slice.slice_type === 'best_seller_recommendations'
      )
        ?.primary as CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary
    )?.category ??
    null;

  const [sliceProducts, slicePromotions, compareTables] = await Promise.all([
    getSliceProducts(categoryPage.body, locale),
    getSlicePromotions(categoryPage.body, locale),
    getCompareTables(categoryPage.body),
  ]);

  return {
    props: {
      stores,
      categoryPage,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      recommendationsCategory,
      promoBarData,
      sliceProducts,
      slicePromotions,
      compareTables,
    },
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function CategoryPageComponent({
  categoryPage,
  canonicalUrl,
  langAltUrl,
  xDefault,
  recommendationsCategory,
  sliceProducts,
  slicePromotions,
  compareTables,
}: CategoryPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { meta_title: metaTitle, meta_description: metaDescription } =
    categoryPage;

  const [categoryPageRecommendations, setCategoryPageRecommendations] =
    useState<AlgonomyPlacement[]>([]);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          locale,
        }}
      />

      <RecommendationsLoader
        pageType="categoryLandingPage"
        data={{
          category: recommendationsCategory,
        }}
        onLoad={(data) => {
          setCategoryPageRecommendations(data);
        }}
        trigger={recommendationsCategory !== undefined}
      />

      <CategoryPageSliceZone
        slices={categoryPage?.body}
        categoryPageRecommendations={categoryPageRecommendations}
        sliceProducts={sliceProducts}
        slicePromotions={slicePromotions}
        compareTables={compareTables}
      />
    </>
  );
}

CategoryPageComponent.Layout = Layout;
